import { useGetCompanyProfile } from "@/api/app/profile/useGetCompanyProfile";
import DatePicker from "@/components/DatePicker";
import InputGroup from "@/components/InputGroup";
import LoadingScreen from "@/components/Loading";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  CompanyProfileEdit,
  companyProfileEditSchema,
} from "@/schema/app/profile/companyProfileEdit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CompanyFormActionButtons from "./CompanyFormActionButtons";

function CompanyForm() {
  const { data, isLoading } = useGetCompanyProfile();
  const { t } = useTranslation();

  const methods = useForm<CompanyProfileEdit>({
    resolver: zodResolver(companyProfileEditSchema),
    values: data?.data as CompanyProfileEdit,
  });

  if (isLoading) return <LoadingScreen />;
  if (!data) return <div>No Data Found!</div>;

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form>
        {/* heading */}
        <div className="mx-6 mt-8">
          <div className="flex justify-between">
            <div className="my-heading">{t("edit-company-profile")}</div>
            <CompanyFormActionButtons />
          </div>
        </div>

        <div className="mx-6 py-8 flex flex-col gap-4">
          <div className="flex gap-6">
            <div className="w-[320px] text-lg font-bold">
              {t("company-info")}
            </div>
            <div className="my-card grid w-full grid-cols-2 gap-6 px-5 py-4">
              <InputGroup
                // className="col-span-full"
                label={t("company-name")}
                required
                error={errors.company_name?.message}
                register={register("company_name", { required: true })}
              />
              <InputGroup
                // className="col-span-full"
                label={t("company-name-kana")}
                required
                error={errors.company_name_kana?.message}
                register={register("company_name_kana", { required: true })}
              />
              <InputGroup
                label={t("representative-name")}
                required
                error={errors.ceo_name?.message}
                register={register("ceo_name", { required: true })}
              />
              <InputGroup
                label={t("representative-name-kana")}
                required
                error={errors.ceo_name_kana?.message}
                register={register("ceo_name_kana", { required: true })}
              />
              <InputGroup
                type="text"
                label={t('position')}
                className="col-span-full"
                required
                error={errors.position?.message}
                register={register("position", { required: true })}
              />
              <InputGroup
                className="col-span-full"
                label={t("location")}
                error={errors.company_address?.message}
                register={register("company_address")}
              />
              <InputGroup
                label={t("phone")}
                error={errors.phone_number?.message}
                register={register("phone_number")}
              />
              <InputGroup
                label={t("url")}
                error={errors.homepage_url?.message}
                register={register("homepage_url")}
              />
            </div>
          </div>
          <div className="h-px bg-cool-gray-300" />
          <div className="flex gap-6">
            <div className="w-[320px] text-lg font-bold">
              {t("coporate-profile")}
            </div>
            <div className="my-card grid w-full grid-cols-2 gap-6 px-5 py-4">
              <div className="flex flex-col gap-1">
                <div>{t("established-date")}</div>
                <Controller
                  control={control}
                  name="established_date"
                  render={({ field }) => (
                    <DatePicker
                      className="w-full border border-cool-gray-300"
                      value={field.value}
                      error={errors.established_date?.message}
                      onDateChange={field.onChange}
                    />
                  )}
                />
              </div>
              <InputGroup
                type="email"
                required
                label={t('email')}
                error={errors.email?.message}
                register={register("email", { required: true })}
              />
              <InputGroup
                type="text"
                label={t("corporate-number")}
                error={errors.corporate_number?.message}
                register={register("corporate_number")}
              />
              <InputGroup
                type="number"
                label={t("capital")}
                error={errors.funds?.message}
                register={register("funds")}
              />
              <InputGroup
                type="text"
                label={t("market-name")}
                required
                error={errors.market_name?.message}
                register={register("market_name")}
              />
              <InputGroup
                type="number"
                label={t("num-of-employees")}
                error={errors.employee_count?.message}
                register={register("employee_count")}
              />
              <InputGroup
                type="number"
                label={t("foreign-employees")}
                required
                error={errors.foreign_employee_count?.message}
                register={register("foreign_employee_count")}
              />
              <InputGroup
                type="number"
                label={t("permanent-employees")}
                required
                error={errors.permanent_employee_count?.message}
                register={register("permanent_employee_count")}
              />
              <InputGroup
                type="number"
                label={t("contract-employees")}
                required
                error={errors.contract_employee_count?.message}
                register={register("contract_employee_count")}
              />
              <InputGroup
                type="number"
                label={t("temporary-employees")}
                required
                error={errors.temporary_employee_count?.message}
                register={register("temporary_employee_count")}
              />
              <InputGroup
                type="number"
                label={t("contractor-employees")}
                required
                error={errors.contractor_count?.message}
                register={register("contractor_count")}
              />
              <InputGroup
                type="number"
                label={t("part-time-employees")}
                required
                error={errors.part_time_employee_count?.message}
                register={register("part_time_employee_count")}
              />
              <InputGroup
                type="number"
                label={t("intern-employees")}
                required
                error={errors.intern_count?.message}
                register={register("intern_count")}
              />
              <InputGroup
                type="number"
                label={t("specialist-employees")}
                required
                error={errors.specialist_count?.message}
                register={register("specialist_count")}
              />
              <InputGroup
                type="text"
                className="col-span-full"
                label={t("employee-countries")}
                required
                error={errors.employee_countries?.message}
                register={register("employee_countries")}
              />
              <div className="col-span-full">
                <Controller
                  control={control}
                  name="is_listed"
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <Label label={t("listed-market")} htmlFor="is_listed" />
                      <Checkbox
                        id="is_listed"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
          <div className="h-px bg-cool-gray-300" />
          <CompanyFormActionButtons />
        </div>
      </form>
    </FormProvider>
  );
}

export default CompanyForm;
